import { useState, useEffect, CSSProperties } from 'react';
import { CurrentBrand, OrganizationUser } from 'modules/Types';
import { Body2, Subtitle1, Subtitle2 } from 'components/shareable/Typography';
import useScreen from 'Hooks/useScreen';
import useUser from 'Hooks/useUser';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useBrand from 'Hooks/useBrand';
import { toast } from 'react-toastify';
import { TOAST_SUCCESS_OPTIONS } from '../../../pages/OrganizationPage/Organization_constants';
import { Container } from 'modules/Sty';
import { UserPermissionOnBrand } from 'modules/Const';
import { toastError } from 'modules/Toast';
import Colors from 'modules/Colors';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import AdminSearchSelectorModal from 'components/ProfileDropdown/AdminSearchSelectorModal';
import InputSymphony from 'elements/InputSymphony';
import { clsx, styx } from 'modules/Utils';
import { ReactComponent as UserIcon } from 'assets/images/user.svg';
import { getBrandUsers_api, BrandUser } from 'api/brandApi';

interface AdminAddExistingBrandModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface UserWithPermission {
    id: number;
    name?: string;
    lastName?: string;
    email?: string;
    permissionLevel: UserPermissionOnBrand;
}

type ModalStep = 'brand-selection' | 'existing-users-permissions' | 'org-users-permissions';

const AdminAddExistingBrandModal = ({
    isOpen,
    onClose,
}: AdminAddExistingBrandModalProps) => {
    const { mobileView } = useScreen();
    const { currentUser } = useUser();
    const { organization, organizationUsers, adminAddExistingBrandToOrganization } = useOrganizationV2();
    
    // Modal flow state
    const [currentStep, setCurrentStep] = useState<ModalStep>('brand-selection');
    const [selectedBrand, setSelectedBrand] = useState<CurrentBrand | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    // Brand users state
    const [existingBrandUsers, setExistingBrandUsers] = useState<BrandUser[]>([]);
    const [existingUsersPermissions, setExistingUsersPermissions] = useState<UserWithPermission[]>([]);
    const [loadingBrandUsers, setLoadingBrandUsers] = useState<boolean>(false);
    
    // Organization users permissions state
    const [orgUsersPermissions, setOrgUsersPermissions] = useState<UserWithPermission[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep('brand-selection');
            setSelectedBrand(null);
            setExistingBrandUsers([]);
            setExistingUsersPermissions([]);
            setOrgUsersPermissions([]);
            setSearchTerm('');
        }
    }, [isOpen]);

    // Load existing brand users when brand is selected
    useEffect(() => {
        if (selectedBrand && currentStep === 'existing-users-permissions' && organizationUsers) {
            loadBrandUsers();
        }
    }, [selectedBrand, currentStep, organizationUsers]);

    // Initialize organization users permissions when reaching step 3
    useEffect(() => {
        if (currentStep === 'org-users-permissions' && organizationUsers) {
            const orgUsersWithDefaultPermissions: UserWithPermission[] = organizationUsers.map(user => ({
                id: user.id,
                name: user.name,
                lastName: user.lastName,
                email: user.email,
                permissionLevel: UserPermissionOnBrand.NONE // Default to no access
            }));
            setOrgUsersPermissions(orgUsersWithDefaultPermissions);
        }
    }, [currentStep, organizationUsers]);

    const loadBrandUsers = async () => {
        if (!selectedBrand) return;

        try {
            setLoadingBrandUsers(true);
            const response = await getBrandUsers_api({ brandId: selectedBrand.id });
            const allBrandUsers = response.users || [];
            
            // Get organization user emails for deduplication
            const orgUserEmails = new Set(
                organizationUsers?.map(orgUser => orgUser.email?.toLowerCase()).filter(Boolean) || []
            );
            
            console.log('🔧 Admin: Deduplicating brand users:', {
                totalBrandUsers: allBrandUsers.length,
                orgUserEmails: Array.from(orgUserEmails),
            });

            // Filter out brand users who are already organization members
            const brandUsersNotInOrg = allBrandUsers.filter((user: BrandUser) => {
                const userEmail = user.email?.toLowerCase();
                const isInOrg = userEmail && orgUserEmails.has(userEmail);
                
                if (isInOrg) {
                    console.log('🔧 Admin: Filtering out duplicate user:', { email: user.email, name: user.name });
                }
                
                return !isInOrg;
            });
            
            console.log('🔧 Admin: After deduplication:', {
                originalCount: allBrandUsers.length,
                filteredCount: brandUsersNotInOrg.length,
                removedCount: allBrandUsers.length - brandUsersNotInOrg.length
            });
            
            setExistingBrandUsers(brandUsersNotInOrg);

            // Initialize permissions for existing users (default to Brand Admin)
            const usersWithPermissions: UserWithPermission[] = brandUsersNotInOrg.map((user: BrandUser) => ({
                id: parseInt(user.id), // Convert string id to number
                name: user.name,
                lastName: undefined, // BrandUser doesn't have lastName
                email: user.email, // Optional email field
                permissionLevel: UserPermissionOnBrand.ADMINISTRATOR // Default to Brand Admin
            }));
            setExistingUsersPermissions(usersWithPermissions);
        } catch (error) {
            console.error('Error loading brand users:', error);
            toastError('Failed to load brand users');
        } finally {
            setLoadingBrandUsers(false);
        }
    };

    const handleBrandSelection = (brands: CurrentBrand[]) => {
        console.log('🔧 Admin: Brand selected:', brands);
        if (brands.length > 0) {
            setSelectedBrand(brands[0]);
            setCurrentStep('existing-users-permissions');
            console.log('🔧 Admin: Moving to step 2 - existing users permissions');
        }
    };

    const handleNextFromExistingUsers = () => {
        setCurrentStep('org-users-permissions');
    };

    const handleBackToExistingUsers = () => {
        setCurrentStep('existing-users-permissions');
    };

    const handleBackToBrandSelection = () => {
        setCurrentStep('brand-selection');
        setSelectedBrand(null);
    };

    const updateExistingUserPermission = (userId: number, permissionLevel: UserPermissionOnBrand) => {
        setExistingUsersPermissions(prev => 
            prev.map(user => 
                user.id === userId ? { ...user, permissionLevel } : user
            )
        );
    };

    const updateOrgUserPermission = (userId: number, permissionLevel: UserPermissionOnBrand) => {
        setOrgUsersPermissions(prev => 
            prev.map(user => 
                user.id === userId ? { ...user, permissionLevel } : user
            )
        );
    };

    const handleFinish = async () => {
        if (!selectedBrand || !organization) {
            return;
        }

        try {
            setIsLoading(true);

            // Prepare permissions data
            const existingUserPermissions = existingUsersPermissions.map(user => ({
                userId: user.id,
                permissionLevel: user.permissionLevel
            }));

            const organizationUserPermissions = orgUsersPermissions
                .filter(user => user.permissionLevel !== UserPermissionOnBrand.NONE) // Only include users with permissions
                .map(user => ({
                    userId: user.id,
                    permissionLevel: user.permissionLevel
                }));

            await adminAddExistingBrandToOrganization({
                organizationId: organization.id,
                brandId: selectedBrand.id,
                existingUserPermissions,
                organizationUserPermissions
            });

            toast.success(
                `"${selectedBrand.name}" has been successfully added to "${organization.name}"!`,
                TOAST_SUCCESS_OPTIONS
            );

            onClose();
        } catch (error: any) {
            console.error('Error adding brand to organization:', error);
            toastError(error?.response?.data?.message || 'Failed to add brand to organization');
        } finally {
            setIsLoading(false);
        }
    };

    // Render step 1: Brand selection
    const renderBrandSelection = () => {
        return (
            <AdminSearchSelectorModal
                mode="select"
                open={isOpen && currentStep === 'brand-selection'}
                onImportSelected={handleBrandSelection}
                closeOnSelection={false}
                onClose={onClose}
            />
        );
    };

    // Render step 2: Existing brand users permissions
    const renderExistingUsersPermissions = () => {
        console.log('🔧 renderExistingUsersPermissions called:', { 
            currentStep, 
            shouldRender: currentStep === 'existing-users-permissions',
            selectedBrand: selectedBrand?.name,
            loadingBrandUsers,
            existingUsersCount: existingUsersPermissions.length
        });
        
        if (currentStep !== 'existing-users-permissions') return null;

        return (
            <ModalSymphony
                className="adminAddExistingBrandStep2Modal"
                isOpen={true}
                fullScreen={mobileView}
                onClose={onClose}
                title={`Set Permissions for "${selectedBrand?.name}" Users`}
                subtitle="Configure permissions for brand users who are not already in your organization"
                headerIcon={<UserIcon style={{ width: 32, height: 32 }} />}
                maxWidth={600}
            >
                <Container style={sty.container}>
                    <Body2 color={Colors.greyDark} style={{ marginBottom: 16 }}>
                        These are brand users who are <strong>not already in your organization</strong>. They will be invited as organization members with the selected brand permissions.
                    </Body2>

                    {loadingBrandUsers ? (
                        <div style={sty.loadingContainer}>
                            <Body2>Loading brand users...</Body2>
                        </div>
                    ) : existingUsersPermissions.length === 0 ? (
                        <div style={sty.emptyState}>
                            <Body2 color={Colors.greyDark}>
                                No brand users need to be added - all existing brand users are already in your organization.
                            </Body2>
                        </div>
                    ) : (
                        <div className="existingUsersListContainer" style={sty.usersListContainer}>
                            {existingUsersPermissions.map((user) => (
                                <div key={user.id} className="userRow" style={sty.userRow}>
                                    <div className="userRowLeft" style={sty.userRowLeft}>
                                        <div className="userInfo">
                                            <Subtitle2>
                                                {user.name && user.lastName
                                                    ? `${user.name} ${user.lastName}`
                                                    : user.email
                                                }
                                            </Subtitle2>
                                            {user.name && user.lastName && (
                                                <Body2 color={Colors.greyDark}>{user.email}</Body2>
                                            )}
                                        </div>
                                    </div>
                                    <div className="userRowRight" style={sty.userRowRight}>
                                        <InputSymphony
                                            className="userPermissionSelect"
                                            value={user.permissionLevel}
                                            onChange={(value) => updateExistingUserPermission(user.id, value as UserPermissionOnBrand)}
                                            options={[
                                                { value: UserPermissionOnBrand.ADMINISTRATOR, title: 'Brand Admin' },
                                                { value: UserPermissionOnBrand.VIEW_ONLY, title: 'View Only' },
                                            ]}
                                            isSelectDropdown={true}
                                            style={{ minWidth: 140 }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="buttonActionRow" style={sty.buttonActionRow}>
                        <ButtonSymphony
                            className="backButton"
                            variant="outlined"
                            onClick={handleBackToBrandSelection}
                        >
                            Back
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="nextButton"
                            onClick={handleNextFromExistingUsers}
                        >
                            Next: Set Organization User Access
                        </ButtonSymphony>
                    </div>
                </Container>
            </ModalSymphony>
        );
    };

    // Render step 3: Organization users permissions
    const renderOrgUsersPermissions = () => {
        if (currentStep !== 'org-users-permissions') return null;

        // Filter users based on search term
        const filteredOrgUsers = orgUsersPermissions.filter(user => {
            if (!searchTerm) return true;
            const fullName = `${user.name || ''} ${user.lastName || ''}`.toLowerCase();
            const email = user.email?.toLowerCase() || '';
            return fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());
        });

        return (
            <ModalSymphony
                className="adminAddExistingBrandStep3Modal"
                isOpen={true}
                fullScreen={mobileView}
                onClose={onClose}
                title={`Set Team Member Access to "${selectedBrand?.name}"`}
                subtitle="Configure brand access for all existing organization members (including any who were already brand users)"
                headerIcon={<UserIcon style={{ width: 32, height: 32 }} />}
                maxWidth={600}
            >
                <Container style={sty.container}>
                    {/* Search */}
                    <div style={sty.searchContainer}>
                        <InputSymphony
                            className="searchUsersInput"
                            type="text"
                            value={searchTerm}
                            placeholder="Search team members"
                            onChange={setSearchTerm}
                            label="Search Team Members"
                            isSearch
                        />
                    </div>

                    {/* Users List */}
                    <div className="orgUsersListContainer" style={sty.usersListContainer}>
                        {filteredOrgUsers.length === 0 ? (
                            <Body2 color={Colors.greyDark}>
                                {searchTerm ? 'No team members found matching your search.' : 'No team members in this organization.'}
                            </Body2>
                        ) : (
                            filteredOrgUsers.map((user) => (
                                <div key={user.id} className="userRow" style={sty.userRow}>
                                    <div className="userRowLeft" style={sty.userRowLeft}>
                                        <div className="userInfo">
                                            <Subtitle2>
                                                {user.name && user.lastName
                                                    ? `${user.name} ${user.lastName}`
                                                    : user.email
                                                }
                                                {user.id === currentUser?.id && (
                                                    <span style={{ color: Colors.greyDark, fontSize: 12, marginLeft: 8 }}>
                                                        (You)
                                                    </span>
                                                )}
                                            </Subtitle2>
                                            {user.name && user.lastName && (
                                                <Body2 color={Colors.greyDark}>{user.email}</Body2>
                                            )}
                                        </div>
                                    </div>
                                    <div className="userRowRight" style={sty.userRowRight}>
                                        <InputSymphony
                                            className="userPermissionSelect"
                                            value={user.permissionLevel}
                                            onChange={(value) => updateOrgUserPermission(user.id, value as UserPermissionOnBrand)}
                                            options={[
                                                { value: UserPermissionOnBrand.ADMINISTRATOR, title: 'Brand Admin' },
                                                { value: UserPermissionOnBrand.VIEW_ONLY, title: 'View Only' },
                                                { value: UserPermissionOnBrand.NONE, title: 'No Access' }
                                            ]}
                                            isSelectDropdown={true}
                                            style={{ minWidth: 140 }}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="buttonActionRow" style={sty.buttonActionRow}>
                        <ButtonSymphony
                            className="backButton"
                            variant="outlined"
                            onClick={handleBackToExistingUsers}
                        >
                            Back
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="finishButton"
                            isLoading={isLoading}
                            onClick={handleFinish}
                            disabled={isLoading}
                        >
                            Add Brand to Organization
                        </ButtonSymphony>
                    </div>
                </Container>
            </ModalSymphony>
        );
    };

    // Don't render AdminSearchSelectorModal wrapper when on other steps
    if (currentStep !== 'brand-selection') {
        return (
            <>
                {renderExistingUsersPermissions()}
                {renderOrgUsersPermissions()}
            </>
        );
    }

    return renderBrandSelection();
};

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%'
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        width: '100%'
    },
    usersListContainer: {
        maxHeight: 350,
        overflowY: 'auto',
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
        marginBottom: 16
    },
    userRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        backgroundColor: Colors.white,
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    userRowLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    userRowRight: {
        display: 'flex',
        alignItems: 'center',
    },
    buttonActionRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        marginTop: 16,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 32
    },
    emptyState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 32,
        textAlign: 'center'
    }
};

export default AdminAddExistingBrandModal;
